import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import useAuth from 'hooks/useAuth';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import axios from '../../../utils/axios';
import { isMaster, podeGravarCadastroAdmin, podeSubstituirArquivoApk } from '../../../utils/perfil';
import { Num, PillStatus } from '../../../components/gps';
import PainelLista, { Coluna } from '../../../components/gps/PainelLista';
import MenuAcoes, { ResumoExclusao } from '../../../components/gps/MenuAcoes';
import Modal, { AreaArquivo, BotaoSecundario } from '../../../components/gps/Modal';
import ModalApk from '../../../components/gps/ModalApk';
import { linhasIniciais } from '../../../components/gps/paginacao';

/* ============================================================================
   APKs - visual novo (shadcn + Tailwind).

   Contrato conferido em remoteAPI/src/app/apks/apks.service.ts:50:
     GET /apks/paginado?page&rows&valor&campo&direcao&ordenar&status
     -> { content: ApkEntity[] com status ja convertido para "ATIVO"/"INATIVO",
          totalElements }
   Campos da entidade: codigo, nome, status, packageName, versionName,
   versionCode, url, fabricante, tipo, createdAt.

   PARTICULARIDADE: o service usa `.withDeleted()`, ou seja, APK apagada continua
   na listagem (marcada como INATIVA). Nao e engano - o arquivo continua servido
   e um aparelho antigo pode estar apontando para ele. Por isso a coluna VERSAO
   mostra versionName E versionCode juntos: o `versionCode` e o que o backend usa
   para recusar duplicata, e e nele que o suporte precisa olhar quando um aparelho
   "nao atualiza".

   `campo` e `ordenar` viram propriedade do QueryBuilder - so constantes daqui.
   ============================================================================ */

type Apk = {
  codigo?: number;
  nome?: string;
  status?: number | string;
  packageName?: string;
  versionName?: string;
  versionCode?: string;
  fabricante?: string;
  url?: string;
  createdAt?: string;
};

const STATUS = { TODOS: -1, ATIVO: 1, INATIVO: 0 } as const;

function numero(valor: unknown) {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
}

function ehAtivo(v: unknown) {
  return String(v).toUpperCase() === 'ATIVO' || Number(v) === 1;
}

Apks.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function Apks() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [menuAberto, setMenuAberto] = useState<number | null>(null);
  const [apagando, setApagando] = useState<Apk | null>(null);
  const [removendo, setRemovendo] = useState(false);
  const [substituindo, setSubstituindo] = useState<Apk | null>(null);
  // `undefined` = fechado; '0' = criar; qualquer outro = editar aquele codigo.
  const [editando, setEditando] = useState<string | undefined>(undefined);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [linhas, setLinhas] = useState<Apk[]>([]);
  const [total, setTotal] = useState(0);
  // Recarrega os cartoes (Total/Ativos) depois de apagar ou salvar - antes so na montagem.
  const [versaoIndices, setVersaoIndices] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [busca, setBusca] = useState('');
  /* ATIVOS por padrao, e nao "Todos" - pedido de 25/08/2026.

     O trabalho do dia a dia acontece sobre o que esta ativo; os inativos sao
     historico. Abrindo em "Todos", a primeira pagina vinha misturada e era preciso
     filtrar toda vez para chegar ao que interessa.

     Os CONTADORES dos tres segmentos continuam vindo completos (vem de endpoint
     proprio de indices), entao o total geral segue visivel e "Todos" e um clique. */
  const [status, setStatus] = useState<number>(STATUS.ATIVO);
  const [linhasPorPagina, setLinhasPorPagina] = useState(linhasIniciais);
  // Vira propriedade do QueryBuilder (`apk.${ordenar}`).
  const [ordenarPor, setOrdenarPor] = useState('createdAt');
  const [direcao, setDirecao] = useState<'asc' | 'desc'>('desc');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [indices, setIndices] = useState({ total: 0, ativos: 0, inativos: 0 });

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      // Mesma caixa unica das outras telas: pacote tem ponto, nome nao.
      const campo = busca.includes('.') ? 'packageName' : 'nome';

      const resposta = await axios.get<{ content: Apk[]; totalElements: number }>(
        `/apks/paginado?page=${pagina}&rows=${linhasPorPagina}` +
          `&valor=${encodeURIComponent(busca.trim())}&campo=${campo}` +
          `&direcao=${direcao}&ordenar=${ordenarPor}&status=${status}`
      );
      setLinhas(resposta.data?.content || []);
      setTotal(numero(resposta.data?.totalElements));
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar as APKs.');
      setLinhas([]);
      setTotal(0); // sem isto o rodape seguia "Mostrando 0 de N" com a paginacao ativa
    } finally {
      setCarregando(false);
    }
  }, [pagina, busca, status, linhasPorPagina, ordenarPor, direcao]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    axios
      .get<any>('/apks/findByStatus')
      .then((r) => {
        const d = r.data || {};
        setIndices({ total: numero(d.total), ativos: numero(d.ativo), inativos: numero(d.inativo) });
      })
      .catch(() => setIndices({ total: 0, ativos: 0, inativos: 0 }));
  }, [versaoIndices]);

  const colunas: Array<Coluna<Apk>> = useMemo(
    () => [
      {
        chave: 'nome',
        ordenarPor: 'nome',
        exportar: (a) => a.nome ?? '',
        rotulo: 'APK',
        largura: 'minmax(200px,2.2fr)',
        esqueleto: 'w-40',
        render: (a) => (
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="block truncate text-[12.5px] font-semibold">{a.nome || ' - '}</span>
          </div>
        ),
      },
      {
        chave: 'pacote',
        ordenarPor: 'packageName',
        exportar: (a) => a.packageName ?? '',
        rotulo: 'Pacote',
        largura: 'minmax(180px,2fr)',
        esqueleto: 'w-48',
        render: (a) => (
          <span className="block truncate text-[12px] text-muted-foreground">{a.packageName || '—'}</span>
        ),
      },
      {
        chave: 'versao',
        ordenarPor: 'versionCode',
        exportar: (a) => [a.versionName ?? '', a.versionCode ?? ''].join(' '),
        rotulo: 'Versão',
        largura: '150px',
        esqueleto: 'w-20',
        render: (a) => (
          <span className="flex min-w-0 items-baseline gap-1.5">
            <span className="block truncate text-[12px] font-semibold">{a.versionName || '—'}</span>
            {a.versionCode && (
              <Num className="rounded bg-soft px-1 text-[11px] text-muted-foreground">{a.versionCode}</Num>
            )}
          </span>
        ),
      },
      {
        chave: 'fabricante',
        ordenarPor: 'fabricante',
        exportar: (a) => a.fabricante ?? '',
        rotulo: 'Fabricante',
        largura: 'minmax(120px,1fr)',
        esqueleto: 'w-24',
        render: (a) => <span className="block truncate text-[12px] text-muted-foreground">{a.fabricante || '—'}</span>,
      },
      {
        chave: 'status',
        ordenarPor: 'status',
        exportar: (a) => (ehAtivo(a.status) ? 'ATIVA' : 'INATIVA'),
        rotulo: 'Status',
        largura: '110px',
        esqueleto: 'w-16',
        render: (a) => <PillStatus ativo={ehAtivo(a.status)} />,
      },
      {
        chave: 'acao',
        rotulo: 'Opções',
        largura: '80px',
        alinhar: 'dir',
        esqueleto: 'nenhum',
        render: (a) => (
          <MenuAcoes<Apk>
            linha={a}
            aberto={menuAberto === a.codigo}
            onAlternar={() => setMenuAberto(menuAberto === a.codigo ? null : a.codigo ?? null)}
            onFechar={() => setMenuAberto(null)}
            ancorarADireita
            acoes={[
              {
                rotulo: 'Abrir / editar',
                ao: (x) => x.codigo && setEditando(String(x.codigo)),
              },
              {
                // Substituir o binario MANTENDO a URL de download - e por isso
                // que existe: aparelho antigo aponta para a URL antiga.
                // `podeSubstituirArquivoApk` tem prazo (utils/perfil.ts), nao e
                // permissao permanente.
                rotulo: 'Substituir arquivo',
                visivel: podeSubstituirArquivoApk(user),
                ao: (x) => {
                  setArquivo(null);
                  setSubstituindo(x);
                },
              },
              {
                rotulo: 'Apagar',
                perigo: true,
                /* SO MASTER (10/09/2026, a pedido).

                   Estava em `podeGravarCadastroAdmin`, que inclui Administrador - o
                   mesmo teste usado para CRIAR e EDITAR. Apagar nao pertence a essa
                   categoria: criar errado se corrige editando, apagar nao tem desfazer
                   e leva junto os vinculos do registro.

                   A autoridade continua sendo o backend; isto so evita mostrar uma
                   opcao que a pessoa nao deveria acionar. */
                /* ESCONDIDO (29/09/2026): A ROTA NAO EXISTE.

                   O botao chamava DELETE /apks/:codigo, e apks.controller.ts nao
                   tem @Delete - importa o decorador e nunca o usa. O modal dizia
                   "o registro sai da listagem", e a resposta era 404.

                   POR QUE NAO CRIEI A ROTA: TAB_APK nao tem coluna DELETED_AT
                   (conferido no banco), entao apagar seria exclusao FISICA - e os
                   aparelhos baixam a APK pela URL deste registro. Apagar deixaria
                   aparelho em campo sem conseguir atualizar, sem volta.

                   Para tirar uma APK de uso hoje, o caminho que funciona e
                   INATIVAR. O modal de confirmacao ficou no arquivo: implementada
                   a rota (de preferencia com soft-delete), basta voltar esta linha
                   para `isMaster(user)`. */
                visivel: false,
                ao: (x) => setApagando(x),
              },
            ]}
          />
        ),
      },
    ],
    [menuAberto, user]
  );

  const apagar = async () => {
    if (!apagando?.codigo) return;
    setRemovendo(true);
    try {
      await axios.delete(`/apks/${apagando.codigo}`);
      enqueueSnackbar('APK apagada.', { variant: 'success' });
      setApagando(null);
      setVersaoIndices((v) => v + 1);
      // Era o unico item de uma pagina que nao e a primeira: volta uma pagina.
      // Sem isto a tela ficava em "Nenhum ... encontrado" com registros antes.
      if (linhas.length === 1 && pagina > 0) setPagina((p) => p - 1);
      else await carregar();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Não foi possível apagar a APK.', { variant: 'error' });
    } finally {
      setRemovendo(false);
    }
  };

  // Envia o novo binario para a MESMA URL de download. O backend usa o nome de
  // arquivo do proprio registro, valida que o packageName confere e guarda backup
  // do arquivo anterior antes de sobrescrever.
  const substituirArquivo = async () => {
    if (!substituindo?.codigo || !arquivo) return;
    setEnviando(true);
    try {
      const form = new FormData();
      form.append('file', arquivo);
      const { data } = await axios.put<any>(`/apks/substituir-arquivo/${substituindo.codigo}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      enqueueSnackbar(
        `Arquivo substituído. Versão ${data?.versaoAnterior} para ${data?.versionCode}. A URL de download não mudou.`,
        { variant: 'success', style: { whiteSpace: 'pre-line' } }
      );
      setSubstituindo(null);
      setArquivo(null);
      await carregar();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Ops ocorreu um erro no sistema.', { variant: 'error' });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Page title="APKs">
      <PainelLista<Apk>
        titulo="APKs"
        descricao={
          <>
            <Num>{(indices.total || total).toLocaleString('pt-BR')}</Num> pacotes hospedados. O{' '}
            <Num>versionCode</Num> é o número que o backend usa para recusar upload repetido — é nele que
            se olha quando um aparelho não atualiza.
          </>
        }
        acao={
          <button
            type="button"
            onClick={() => setEditando('0')}
            className="h-9 flex-none rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground"
          >
            + Nova APK
          </button>
        }
        indicadores={[
          { rotulo: 'Total', valor: indices.total || total, legenda: 'hospedadas', tom: 'info' },
          {
            rotulo: 'Ativas',
            valor: indices.ativos,
            legenda: 'distribuindo',
            tom: 'ok',
            base: indices.total || total,
          },
          {
            rotulo: 'Inativas',
            valor: indices.inativos,
            legenda: 'inclui apagadas',
            tom: 'danger',
            base: indices.total || total,
          },
        ]}
        busca={busca}
        aoBuscar={(v) => {
          setPagina(0);
          setBusca(v);
        }}
        placeholderBusca="Nome ou pacote"
        segmentos={[
          { valor: STATUS.TODOS as number, rotulo: 'Todas', total: indices.total || total },
          { valor: STATUS.ATIVO as number, rotulo: 'Ativas', total: indices.ativos },
          { valor: STATUS.INATIVO as number, rotulo: 'Inativas', total: indices.inativos },
        ]}
        segmentoAtual={status}
        aoTrocarSegmento={(v) => {
          setPagina(0);
          setStatus(v);
        }}
        ordenarPor={ordenarPor}
        direcao={direcao}
        aoOrdenar={(campo) => {
          if (campo === ordenarPor) setDirecao((v) => (v === 'asc' ? 'desc' : 'asc'));
          else { setOrdenarPor(campo); setDirecao('asc'); }
          setPagina(0);
        }}
        linhasPorPagina={linhasPorPagina}
        aoTrocarLinhasPorPagina={(n) => { setPagina(0); setLinhasPorPagina(n); }}
        aoLimparFiltros={() => { setBusca(''); setStatus(STATUS.TODOS); setOrdenarPor('createdAt'); setDirecao('desc'); setPagina(0); }}
        nomeArquivo="apks"
        colunas={colunas}
        linhas={linhas}
        chaveLinha={(a, i) => String(a.codigo ?? `sem-codigo-${i}`)}
        /* 🔴 O clique na linha ia para a TELA ANTIGA em MUI, enquanto o menu da
           mesma linha abria o modal novo. O modal (`ModalApk`) ja estava montado
           nesta pagina - faltava a linha usar ele. O mesmo defeito existia nas
           listas de grupos, usuarios e regionais: sempre que a tela nova nasceu ao
           lado da antiga, o clique da linha ficou apontando para tras. */
        aoClicarLinha={(a) => a.codigo && setEditando(String(a.codigo))}
        carregando={carregando}
        erro={erro}
        aoRecarregar={carregar}
        vazio="Nenhuma APK encontrada com esse filtro."
        pagina={pagina}
        totalPaginas={Math.max(1, Math.ceil(total / linhasPorPagina))}
        total={total}
        aoTrocarPagina={setPagina}
        rotuloPlural="APKs"
      />

      <ModalApk
        aberto={editando !== undefined}
        codigo={editando}
        onFechar={() => setEditando(undefined)}
        onSalvo={() => { setVersaoIndices((v) => v + 1); carregar(); }}
      />

      <Modal
        aberto={Boolean(substituindo)}
        titulo="Substituir arquivo da APK"
        descricao="O binário troca e a URL de download continua a mesma."
        icone="↥"
        largura="max-w-[520px]"
        onFechar={() => setSubstituindo(null)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setSubstituindo(null)} disabled={enviando}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={substituirArquivo}
              disabled={enviando || !arquivo}
              className="h-9 rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground disabled:opacity-40"
            >
              {enviando ? 'Enviando...' : 'Substituir arquivo'}
            </button>
          </>
        }
      >
        {substituindo && (
          <ResumoExclusao
            titulo={substituindo.nome || '—'}
            detalhe={substituindo.packageName}
            extra={
              <>
                Versão atual: <strong className="text-ink">{substituindo.versionName || '—'}</strong> (
                <Num>{substituindo.versionCode || '—'}</Num>)
              </>
            }
          />
        )}

        <div>
          <span className="mb-1 block text-[12px] font-semibold">
            Novo arquivo .apk <span className="text-danger">*</span>
          </span>
          <AreaArquivo
            arquivo={arquivo}
            onArquivo={setArquivo}
            accept=".apk,application/vnd.android.package-archive"
            extensoes={['.apk']}
            rotulo="Arraste a .apk aqui ou clique para escolher"
            dica="Somente arquivos .apk"
          />
        </div>

        <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px] text-muted-foreground">
          O servidor confere se o <Num>packageName</Num> do arquivo novo é o mesmo do registro e guarda
          backup do anterior antes de sobrescrever. Aparelho que já aponta para esta URL passa a receber o
          arquivo novo.
        </div>
      </Modal>

      <Modal
        aberto={Boolean(apagando)}
        titulo="Apagar APK?"
        descricao="O registro sai da listagem; aparelho que aponte para esta URL deixa de atualizar."
        icone="!"
        largura="max-w-[460px]"
        onFechar={() => setApagando(null)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setApagando(null)} disabled={removendo}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={apagar}
              disabled={removendo}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {removendo ? 'Apagando...' : 'Apagar APK'}
            </button>
          </>
        }
      >
        {apagando && (
          <ResumoExclusao
            titulo={apagando.nome || '—'}
            detalhe={apagando.packageName}
            extra={
              <>
                Versão: <strong className="text-ink">{apagando.versionName || '—'}</strong> (
                <Num>{apagando.versionCode || '—'}</Num>)
              </>
            }
          />
        )}
      </Modal>
    </Page>
  );
}
